import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../lib/supabase';
import { CONCEPTS } from '../data/gameData';
import { toast } from 'sonner';

const Concepts = () => {
  const navigate = useNavigate();
  const [concepts, setConcepts] = useState([]);
  const [learnedConcepts, setLearnedConcepts] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Get current user
      const { user, error: userError } = await auth.getUser();
      if (userError || !user) {
        navigate('/');
        return;
      }

      // Load concepts from local game data
      setConcepts(CONCEPTS);

      // Load learned concepts from Supabase
      const { data: learnedData } = await db.concepts.getAll(user.id);
      setLearnedConcepts(learnedData?.map((c) => c.concept_id) || []);
    } catch (error) {
      console.error('Error loading concepts:', error);
      toast.error('Error al cargar conceptos');
    } finally {
      setLoading(false);
    }
  };

  const handleLearnConcept = async (conceptId) => {
    try {
      // Get current user
      const { user, error: userError } = await auth.getUser();
      if (userError || !user) {
        toast.error('Error al guardar');
        return;
      }

      // Mark as learned in Supabase
      await db.concepts.markLearned(user.id, conceptId);
      setLearnedConcepts([...learnedConcepts, conceptId]);
      toast.success('¡Concepto aprendido!');
    } catch (error) {
      console.error('Error marking concept as learned:', error);
      // If error is because it's already learned, just update the UI
      if (error.message && error.message.includes('duplicate')) {
        setLearnedConcepts([...learnedConcepts, conceptId]);
      }
    }
  };

  const categories = [
    { id: 'all', label: 'Todos' },
    { id: 'nodes', label: 'Nodos' },
    { id: 'workflows', label: 'Flujos' },
    { id: 'automation', label: 'Automatización' },
    { id: 'agents', label: 'Agentes' },
  ];

  const filteredConcepts =
    selectedCategory === 'all' ? concepts : concepts.filter((c) => c.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-heading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="concepts-page">
      {/* Header */}
      <header className="glass border-b border-white/10 sticky top-0 z-50 backdrop-blur-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-text-secondary hover:text-primary transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-heading font-bold text-text-primary">Glosario de Conceptos</h1>
                <p className="text-sm text-text-secondary">
                  Aprendidos: {learnedConcepts.length}/{concepts.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Category Filter */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-button whitespace-nowrap transition-all ${
                selectedCategory === category.id
                  ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-neon'
                  : 'glass hover:border-primary/50'
              }`}
              data-testid={`category-${category.id}`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Concepts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredConcepts.map((concept, index) => (
            <ConceptCard
              key={concept.concept_id}
              concept={concept}
              isLearned={learnedConcepts.includes(concept.concept_id)}
              onClick={() => setSelectedConcept(concept)}
              onLearn={() => handleLearnConcept(concept.concept_id)}
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Concept Detail Modal */}
      {selectedConcept && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedConcept(null)}
          className="fixed inset-0 bg-background/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
          data-testid="concept-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass p-8 rounded-card max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">{selectedConcept.title}</h2>
                <span className="inline-block px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium capitalize">
                  {selectedConcept.category}
                </span>
              </div>
              {learnedConcepts.includes(selectedConcept.concept_id) && (
                <div className="flex items-center gap-2 text-success">
                  <Check className="w-5 h-5" />
                  <span className="text-sm font-medium">Aprendido</span>
                </div>
              )}
            </div>

            <p className="text-lg text-text-primary mb-6">{selectedConcept.description}</p>

            {selectedConcept.related_concepts && selectedConcept.related_concepts.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-heading font-bold text-text-primary mb-2">Conceptos Relacionados</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedConcept.related_concepts.map((relatedId) => {
                    const related = concepts.find((c) => c.concept_id === relatedId);
                    return (
                      related && (
                        <span
                          key={relatedId}
                          className="px-3 py-1 glass rounded-full text-sm text-text-secondary hover:text-primary cursor-pointer transition-colors"
                          onClick={() => setSelectedConcept(related)}
                        >
                          {related.title}
                        </span>
                      )
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedConcept(null)}
                className="flex-1 glass py-3 rounded-lg hover:border-primary/50 transition-all font-heading font-bold"
              >
                Cerrar
              </button>
              {!learnedConcepts.includes(selectedConcept.concept_id) && (
                <button
                  onClick={() => {
                    handleLearnConcept(selectedConcept.concept_id);
                    setSelectedConcept(null);
                  }}
                  className="flex-1 btn-neon py-3 rounded-lg font-heading font-bold"
                >
                  Marcar como Aprendido
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

const ConceptCard = ({ concept, isLearned, onClick, onLearn, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    whileHover={{ y: -4, scale: 1.02 }}
    onClick={onClick}
    className={`glass p-6 rounded-card cursor-pointer transition-all group ${
      isLearned ? 'border-success/50' : 'hover:border-primary/50'
    }`}
    data-testid={`concept-card-${concept.concept_id}`}
  >
    <div className="flex items-start justify-between mb-3">
      <h3 className="text-xl font-heading font-bold text-text-primary group-hover:text-primary transition-colors">
        {concept.title}
      </h3>
      {isLearned && <Check className="w-6 h-6 text-success flex-shrink-0" />}
    </div>
    <p className="text-text-secondary text-sm line-clamp-3 mb-4">{concept.description}</p>
    <div className="flex items-center justify-between">
      <span className="text-xs text-text-muted capitalize">{concept.category}</span>
      {!isLearned && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onLearn();
          }}
          className="text-xs text-primary hover:text-primary-hover font-medium"
        >
          Marcar como aprendido
        </button>
      )}
    </div>
  </motion.div>
);

export default Concepts;